import { slugify } from './slug'

type JsonLd = Record<string, unknown>

const SITE_URL = 'https://theracketcenter.com'

export interface ClubJsonLdInput {
  slug: string
  name: string
  description?: string | null
  city?: string | null
  country?: string | null
  sports?: string[]
  logoUrl?: string | null
}

export function buildClubJsonLd(club: ClubJsonLdInput): JsonLd {
  const hasAddress = Boolean(club.city || club.country)
  return {
    '@context': 'https://schema.org',
    '@type': 'SportsOrganization',
    name: club.name,
    description: club.description ?? undefined,
    url: `${SITE_URL}/clubs/${club.slug}`,
    logo: club.logoUrl ?? undefined,
    address: hasAddress
      ? {
          '@type': 'PostalAddress',
          addressLocality: club.city ?? undefined,
          addressCountry: club.country ?? undefined,
        }
      : undefined,
    sport: club.sports?.length ? club.sports : undefined,
  }
}

export interface TournamentJsonLdInput {
  shortId: string
  name: string
  description?: string | null
  startDate?: string | null
  endDate?: string | null
  city?: string | null
  country?: string | null
  sport?: string | null
  organizerName?: string | null
  organizerSlug?: string | null
}

export function buildTournamentJsonLd(t: TournamentJsonLdInput): JsonLd {
  const hasLocation = Boolean(t.city || t.country)
  const tournamentSlug = t.name ? slugify(t.name) : ''
  const tournamentPath = tournamentSlug
    ? `/tournaments/${encodeURIComponent(t.shortId)}/${encodeURIComponent(tournamentSlug)}`
    : `/tournaments/${encodeURIComponent(t.shortId)}`
  return {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: t.name,
    description: t.description ?? undefined,
    url: `${SITE_URL}${tournamentPath}`,
    startDate: t.startDate ?? undefined,
    endDate: t.endDate ?? undefined,
    sport: t.sport ?? undefined,
    location: hasLocation
      ? {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: t.city ?? undefined,
            addressCountry: t.country ?? undefined,
          },
        }
      : undefined,
    organizer: t.organizerName
      ? {
          '@type': 'Organization',
          name: t.organizerName,
          url: t.organizerSlug ? `${SITE_URL}/clubs/${t.organizerSlug}` : undefined,
        }
      : undefined,
  }
}

/** One published pricing tier, projected into a schema.org `Offer`. */
export interface SoftwareOffer {
  name: string
  /** Numeric price as a string, e.g. '0', '68', '98'. */
  price: string
  priceCurrency: string
  description: string
}

export interface SoftwareApplicationJsonLdInput {
  /** Page path the application is presented on (e.g. '/for-clubs'). */
  path: string
  description: string
  /** Public pricing tiers → schema.org `Offer[]`. Prices are public as of
   *  2026-07-18, so `offers` is now accurate to emit. Advanced is intentionally
   *  omitted upstream (no published numeric price — inventing one would violate
   *  the honesty rail). Omit the field entirely to emit no `offers`. */
  offers?: SoftwareOffer[]
}

/** SoftwareApplication structured data for the B2B marketing pages. Optional
 *  `offers` carries the public pricing tiers (Free/Beginner/Intermediate;
 *  Advanced omitted — no numeric price). No `aggregateRating` — we have no real
 *  review data, and inventing it would be inaccurate (Rich Results warning). */
export function buildSoftwareApplicationJsonLd(input: SoftwareApplicationJsonLdInput): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'The Racket Center',
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Web',
    url: `${SITE_URL}${input.path}`,
    description: input.description,
    offers: input.offers?.length
      ? input.offers.map((offer) => ({
          '@type': 'Offer',
          name: offer.name,
          price: offer.price,
          priceCurrency: offer.priceCurrency,
          description: offer.description,
        }))
      : undefined,
  }
}

export interface FaqItem {
  question: string
  answer: string
}

/** FAQPage structured data (e.g. `/pricing`). Hard rule: the Question /
 *  acceptedAnswer text MUST mirror the visible FAQ verbatim — Google requires
 *  structured data to match the on-page content. Pass the SAME strings the page
 *  renders. */
export function buildFaqPageJsonLd(items: FaqItem[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }
}

export interface OrganizationJsonLdInput {
  description: string
  /** Real registered operating entity — accurate, safe to emit. */
  legalName?: string
}

/** Organization structured data for the home/brand front door. `logo` points at
 *  the real brand mark in `public/`. No `sameAs` — no real social profiles
 *  exist, and inventing them would be inaccurate. The B2B home now ALSO emits
 *  `SoftwareApplication`+offers (public pricing is published); this builder stays
 *  the brand-entity half. */
export function buildOrganizationJsonLd(input: OrganizationJsonLdInput): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'The Racket Center',
    legalName: input.legalName || undefined,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    description: input.description,
  }
}

export interface ArticleJsonLdInput {
  slug: string
  title: string
  description: string
  datePublished: string
}

/** Article structured data for the /guides/<slug> long-form pages. */
export function buildArticleJsonLd(a: ArticleJsonLdInput): JsonLd {
  const org = { '@type': 'Organization', name: 'The Racket Center', url: SITE_URL }
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: a.title,
    description: a.description,
    url: `${SITE_URL}/guides/${a.slug}`,
    datePublished: a.datePublished || undefined,
    author: org,
    publisher: org,
  }
}

export interface CollectionPageJsonLdInput {
  /** Hub path, e.g. '/guides/tournament-formats'. */
  path: string
  name: string
  description: string
  /** The spoke guides the hub curates. */
  items: { slug: string; title: string }[]
  /** Where the items live; `/help` for the Help centre. */
  basePath?: string
}

/** CollectionPage structured data for a promoted /guides/<category> hub — a
 *  curated index of its spoke articles (SEO §5 step 8). Targets the category
 *  head term via name/description; each spoke is a `hasPart` Article. */
export function buildCollectionPageJsonLd(input: CollectionPageJsonLdInput): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: input.name,
    description: input.description,
    url: `${SITE_URL}${input.path}`,
    hasPart: input.items.map((item) => ({
      '@type': 'Article',
      headline: item.title,
      url: `${SITE_URL}${input.basePath ?? '/guides'}/${item.slug}`,
    })),
  }
}

export interface HowToJsonLdInput {
  /** The guide's path, e.g. '/help/create-your-club'. */
  path: string
  name: string
  description: string
  /** YYYY-MM-DD, the day the guide was last checked against the product. */
  dateModified?: string
  steps: { name: string; text?: string }[]
}

/** HowTo structured data for a /help guide: each step links to its `#step-N`. Google no
 *  longer shows HowTo rich results (2023); the markup stays valid and costs nothing. */
export function buildHowToJsonLd(input: HowToJsonLdInput): JsonLd {
  const url = `${SITE_URL}${input.path}`
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: input.name,
    description: input.description,
    url,
    dateModified: input.dateModified || undefined,
    step: input.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text || step.name,
      url: `${url}#step-${index + 1}`,
    })),
  }
}

export interface BreadcrumbItem {
  name: string
  /** Absolute-from-root path (e.g. '/guides' or '/guides#ratings-rankings').
   *  Omit for the current page (the last crumb), which carries no link. */
  path?: string
}

/** BreadcrumbList structured data (schema.org) for the guide trails. The
 *  current page is the last item and may omit `path`. Anchor paths are valid
 *  `item` URLs — the on-page section a non-promoted category points at. */
export function buildBreadcrumbJsonLd(items: BreadcrumbItem[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.path ? `${SITE_URL}${item.path}` : undefined,
    })),
  }
}

export interface PersonJsonLdInput {
  shortId: string
  name: string
  sport?: string | null
}

export function buildPersonJsonLd(p: PersonJsonLdInput): JsonLd {
  const personSlug = p.name ? slugify(p.name) : ''
  const personPath = personSlug
    ? `/players/${encodeURIComponent(p.shortId)}/${encodeURIComponent(personSlug)}`
    : `/players/${encodeURIComponent(p.shortId)}`
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: p.name,
    url: `${SITE_URL}${personPath}`,
    knowsAbout: p.sport ? [p.sport] : undefined,
  }
}
